// SERVER-SIDE PREVIEW route (internal). The robot browser (Cloudflare Browser
// Rendering, driven by the worker's runPreviewRender) navigates here for a
// given order. It downloads the STORED print PDFs and runs the EXACT pipeline
// the admin "Generate previews" backfill used to run in the admin's browser —
// generateAndUploadPreviewAssets (pdfjs rasterize → previews bucket). Reports
// outcome on window.__storizPreviews so the worker can read it.
//
// Gated by the same server-only probe token as /_render-spike. Not linked
// from any UI.
//
// ─── INSTRUMENTATION (#1144, 2026-08-13) ────────────────────────────────────
// This page was a black box. It published exactly one thing — a final
// {done, ok, error} object — and the worker only ever read it AFTER
// `done === true`. So the most common failure, the 230 s waitForFunction
// timeout, discarded EVERYTHING: no stage, no album, no page number, no heap
// figure. "Waiting failed: 230000ms exceeded" was the entire post-mortem.
//
// Two changes fix that, and neither alters what the page does:
//   1. window.__storizPreviews is now a LIVE, mutated-in-place object carrying
//      stage / album / page / heap / a timestamped trail. The worker reads it
//      on the failure path, so a timeout now says exactly where it stalled.
//   2. every step also goes through console.log, which the worker now captures
//      via page.on('console') (serverRender has always done this; this module
//      never did — that asymmetry is why preview crashes left no trace).
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ordersApi, settingsApi } from '@/lib/api';
import { DEFAULT_PRINT_SETTINGS } from '@/lib/assemblyTemplate';
import { renderFetch, renderHeadSource } from '@/lib/renderFetch';
import { applyRenderObjectCache } from '@/lib/renderObjectCacheSwitch';

const PROBE = 'sz-bp-9f2c7a1e8b3d4e';
const TRAIL_MAX = 80;

/** JS heap in MB, when the engine exposes it (Chromium does). Directly tests
 *  the OOM hypothesis: if the trail shows the heap climbing to the limit right
 *  before a "Target closed", the crash is memory and not a bad PDF page. */
function heapMb(): { used: number; total: number; limit: number } | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const m = (performance as any)?.memory;
  if (!m) return null;
  const mb = (b: number) => Math.round(b / 1048576);
  return { used: mb(m.usedJSHeapSize), total: mb(m.totalJSHeapSize), limit: mb(m.jsHeapSizeLimit) };
}

export default function PreviewRenderProbe() {
  const [sp] = useSearchParams();
  const ran = useRef(false);
  const [msg, setMsg] = useState('starting…');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const t0 = Date.now();

    // ONE object, mutated in place for the whole run. The worker holds a live
    // reference through page.evaluate, so every field below is readable at any
    // moment — including mid-crash, which is the entire point.
    const state: {
      done: boolean; ok?: boolean; error?: string; stack?: string;
      stage: string; album: number | null; page: number | null; pages: number | null;
      heap: ReturnType<typeof heapMb>; peakHeapMb: number | null;
      trail: string[]; t: number; ms?: number;
      results?: { album: number; ok: boolean; error?: string }[];
    } = {
      done: false, stage: 'boot', album: null, page: null, pages: null,
      heap: heapMb(), peakHeapMb: null, trail: [], t: Date.now(),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const W = window as any;
    W.__storizPreviews = state;

    const mark = (stage: string, extra?: Record<string, unknown>) => {
      state.stage = stage;
      state.t = Date.now();
      const h = heapMb();
      state.heap = h;
      if (h) state.peakHeapMb = Math.max(state.peakHeapMb ?? 0, h.used);
      if (extra) Object.assign(state, extra);
      const line = `+${((Date.now() - t0) / 1000).toFixed(1)}s ${stage}${h ? ` heap=${h.used}/${h.limit}MB` : ''}`;
      state.trail.push(line);
      if (state.trail.length > TRAIL_MAX) state.trail.shift();
      // Captured by the worker's page.on('console') listener.
      console.log('[preview-probe]', line, extra ? JSON.stringify(extra) : '');
    };

    // A crash inside pdf.js or a canvas op surfaces here and nowhere else.
    window.addEventListener('error', (ev) => {
      state.trail.push(`window.error: ${ev.message}`);
      console.error('[preview-probe] window.error', ev.message);
    });
    window.addEventListener('unhandledrejection', (ev) => {
      state.trail.push(`unhandledrejection: ${String(ev.reason)}`.slice(0, 300));
      console.error('[preview-probe] unhandledrejection', String(ev.reason));
    });

    const order = sp.get('order');
    const albums = (sp.get('albums') ?? '1')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);

    const finish = (ok: boolean, extra?: Record<string, unknown>) => {
      Object.assign(state, extra ?? {});
      state.ms = Date.now() - t0;
      state.ok = ok;
      state.done = true; // set LAST — it is the worker's readiness gate.
      console.log('[preview-probe] DONE', JSON.stringify({ ok, ms: state.ms, peakHeapMb: state.peakHeapMb, results: state.results }));
    };

    (async () => {
      try {
        mark('gate', { order, albums });
        if (sp.get('probe') !== PROBE || !order || albums.length === 0) {
          finish(false, { error: 'bad probe / order / albums' });
          setMsg('forbidden');
          return;
        }
        setMsg('loading order + settings…');
        mark('fetch:order+settings');
        const [{ order: orderRow }, { settings }] = await Promise.all([
          ordersApi.get(order),
          settingsApi.get(),
        ]);
        const printSettings = { ...DEFAULT_PRINT_SETTINGS, ...(settings?.print_template_settings ?? {}) };
        // Download the stored PDFs through the worker's R2 copy when ticked (migration 269).
        applyRenderObjectCache(settings);
        // Same per-album PDF rows the admin card resolves (album_pdfs with a
        // single-album legacy fallback to the order-level urls).
        const rows = (orderRow.album_pdfs ?? []).length > 0
          ? (orderRow.album_pdfs ?? [])
          : ((orderRow.cover_pdf_url && orderRow.interior_pdf_url)
              ? [{ album_index: 1, cover_pdf_url: orderRow.cover_pdf_url, interior_pdf_url: orderRow.interior_pdf_url }]
              : []);
        mark('pdf-rows', { pdfRows: rows.map((r) => r.album_index) });

        mark('import:previewAssets');
        const { generateAndUploadPreviewAssets } = await import('@/lib/previewAssets');
        const results: { album: number; ok: boolean; error?: string }[] = [];
        state.results = results;
        for (const albumIndex of albums) {
          state.album = albumIndex;
          const row = rows.find((r) => r.album_index === albumIndex);
          if (!row?.cover_pdf_url || !row?.interior_pdf_url) {
            mark('album:no-pdfs', { album: albumIndex });
            results.push({ album: albumIndex, ok: false, error: 'PDFs missing — generate PDFs first' });
            continue;
          }
          try {
            setMsg(`album ${albumIndex} — downloading cover…`);
            mark('album:fetch-cover');
            // The cover is one page — buffering it is fine. The INTERIOR is not:
            // a manually-uploaded book can be 300 MB, and calling .blob() on it
            // killed this render browser outright ("Protocol error: Target
            // closed"). Hand pdf.js the URL so it range-streams page by page.
            const coverRes = await renderFetch(row.cover_pdf_url);
            if (!coverRes.ok) throw new Error(`stored cover PDF not reachable (HTTP ${coverRes.status}, signed URL expired?) — regenerate PDFs`);
            mark('album:head-interior');
            // Off: HEAD on the signed link, which pdf.js then reads — as before.
            // On: the worker's link (its HEAD copies the file into R2 first).
            const { res: headRes, url: interiorUrl } = await renderHeadSource(row.interior_pdf_url);
            if (!headRes.ok) throw new Error(`stored interior PDF not reachable (HTTP ${headRes.status}, signed URL expired?) — regenerate PDFs`);
            // Interior size is the single best predictor of whether this album
            // will survive; it was never recorded anywhere.
            const interiorBytes = Number(headRes.headers.get('content-length') ?? 0) || null;
            const coverPdf = await coverRes.blob();
            mark('album:rasterize:start', { interiorMb: interiorBytes ? Math.round(interiorBytes / 1048576) : null, coverMb: Math.round(coverPdf.size / 1048576) });
            setMsg(`album ${albumIndex} — rasterizing…`);
            await generateAndUploadPreviewAssets({
              shopifyOrderId: order,
              albumIndex,
              coverPdf,
              interiorUrl,
              printSettings,
              onProgress: (p) => {
                setMsg(`album ${albumIndex} — ${p.message}`);
                // Every page, so a timeout pinpoints the exact page it died on.
                // The old code sent this to React state only, which nothing
                // outside the DOM could read.
                state.page = p.current;
                state.pages = p.total;
                mark('album:progress', { album: albumIndex, page: p.current, pages: p.total });
              },
            });
            mark('album:done', { album: albumIndex });
            results.push({ album: albumIndex, ok: true });
          } catch (e) {
            const err = e instanceof Error ? e : new Error(String(e));
            mark('album:failed', { album: albumIndex, lastError: err.message });
            console.error('[preview-probe] album failed', albumIndex, err.stack ?? err.message);
            results.push({ album: albumIndex, ok: false, error: err.message });
            // Keep the stack of the FIRST album failure — the worker persists it.
            if (!state.stack) state.stack = err.stack ?? undefined;
          }
        }

        finish(results.length > 0 && results.every((r) => r.ok), { results });
        setMsg('done ✅');
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        mark('fatal', { lastError: err.message });
        console.error('[preview-probe] FATAL', err.stack ?? err.message);
        finish(false, { error: err.message, stack: err.stack });
        setMsg('error');
      }
    })();
  }, [sp]);

  return <div style={{ padding: 20, fontFamily: 'sans-serif' }}>Preview render — {msg}</div>;
}
